/**
 * Profilo tiratore: classe di merito federale e catalogo armi.
 *
 * Due blocchi, entrambi **per disciplina**, perché un atleta può praticare più
 * discipline di tiro (IPSC + Tactical Games) con classi e armi diverse:
 *
 *  1. **Classe di merito** — una select per disciplina, dalle classi ICS
 *     configurate in admin. La scrive sia l'atleta sia il coach (decisione
 *     D14): la assegna la federazione dopo i classifier, e spesso è il coach a
 *     seguire il tesseramento.
 *  2. **Armi** — l'arma è il ponte fra atleta e division: in inserimento
 *     punteggio si sceglie l'arma e la division si *deriva*. Per questo la
 *     division è obbligatoria e il power factor arriva da lei.
 *
 * Due flag distinti, da non confondere:
 *  - `coachMode` — sposta le chiamate su `/coach/athletes/{id}/firearms`. Il
 *    coach **modifica** le configurazioni dei propri atleti, per dare
 *    assistenza: stessa UI, stessa validazione, autorizzazione data dalla
 *    visibilità coach→atleta.
 *  - `readOnly` — sola consultazione, senza scrittura. Serve dove il blocco è
 *    incorporato in una scheda di sola lettura.
 *
 * Spec: docs/shooting/02-domain-model.md §3-4 · 04-frontend-ux.md §3
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAPI } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Loader2, Plus, Star, Trash2 } from "lucide-react";
import { firearmTypeIcon } from "./weaponIcons";
import { toast } from "sonner";

// Vocabolario dei tipi di arma. Quali usa una disciplina è dato admin
// (`firearm_types`): nei Tactical Games se ne usano due nella stessa gara.
const FIREARM_TYPES = [
	{ value: "handgun", label: "Pistola" },
	{ value: "rifle", label: "Carabina" },
	{ value: "shotgun", label: "Fucile" },
	{ value: "pcc", label: "PCC" },
];

const typeLabel = (value) =>
	FIREARM_TYPES.find((t) => t.value === value)?.label || value;

const emptyFirearm = (discipline) => ({
	nickname: "",
	firearm_type: "handgun",
	// Una configurazione per ogni disciplina in cui l'arma viene usata: la
	// stessa pistola serve IPSC e Tactical Games senza registrarla due volte.
	setups: discipline
		? [{ discipline, division: "", power_factor: "", is_default: false }]
		: [],
	make: "",
	model: "",
	caliber: "",
	optic: "",
	notes: "",
});

// Le armi scritte prima dei `setups` hanno i campi piatti: si normalizzano in
// lettura così il form non deve conoscere due forme.
const setupsOf = (firearm) => {
	if (Array.isArray(firearm?.setups) && firearm.setups.length)
		return firearm.setups;
	if (!firearm?.discipline) return [];
	return [
		{
			discipline: firearm.discipline,
			division: firearm.division || "",
			power_factor: firearm.power_factor || "",
			is_default: !!firearm.is_default,
		},
	];
};

export function ShootingProfileSection({
	athleteId,
	shootingSports = [],
	shootingClasses = {},
	onClassesChange,
	// Il coach configura le armi dei propri atleti per dare assistenza: stessa
	// UI, stessa validazione, rotte sotto /coach/athletes/{id}/firearms.
	coachMode = false,
	readOnly = false,
}) {
	const [reference, setReference] = useState({ divisions: [], classes: [] });
	const [firearms, setFirearms] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [draft, setDraft] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [ref, list] = await Promise.all([
				fetchAPI("/shooting/reference").catch(() => ({
					divisions: [],
					classes: [],
				})),
				fetchAPI(
					coachMode
						? `/coach/athletes/${athleteId}/firearms`
						: "/profile/firearms",
				).catch(() => []),
			]);
			setReference({
				divisions: ref.divisions || [],
				classes: ref.classes || [],
			});
			setFirearms(Array.isArray(list) ? list : []);
		} catch (error) {
			toast.error(`Errore nel caricamento: ${error.message}`);
		} finally {
			setLoading(false);
		}
	}, [athleteId, coachMode]);

	useEffect(() => {
		load();
	}, [load]);

	// Unico punto in cui cambia la rotta fra vista atleta e vista coach.
	const firearmsBase = coachMode
		? `/coach/athletes/${athleteId}/firearms`
		: "/profile/firearms";

	const divisionsFor = useCallback(
		(discipline) =>
			reference.divisions.filter((d) => d.discipline === discipline),
		[reference.divisions],
	);

	const classesFor = useCallback(
		(discipline) =>
			reference.classes.filter((c) => c.discipline === discipline),
		[reference.classes],
	);

	const divisionByKey = useMemo(() => {
		const map = {};
		for (const d of reference.divisions) map[`${d.discipline}|${d.value}`] = d;
		return map;
	}, [reference.divisions]);

	const sportLabel = (value) =>
		shootingSports.find((s) => s.value === value)?.label || value;

	const divisionLabel = (discipline, division) =>
		divisionByKey[`${discipline}|${division}`]?.label || division;

	// ── Armi ──

	// Tipi ammessi: il tipo è dell'arma, quindi deve andar bene per **tutte**
	// le discipline in cui viene usata. Con nessuna disciplina scelta si
	// mostrano tutti.
	const typesFor = useCallback(
		(disciplines) => {
			const declaredSets = (disciplines || [])
				.map((d) => shootingSports.find((s) => s.value === d)?.firearm_types)
				.filter((types) => types && types.length);
			if (declaredSets.length === 0) return FIREARM_TYPES;
			return FIREARM_TYPES.filter((t) =>
				declaredSets.every((types) => types.includes(t.value)),
			);
		},
		[shootingSports],
	);

	const draftDisciplines = (draft?.setups || [])
		.map((s) => s.discipline)
		.filter(Boolean);

	// Production e Production Optics si scorano sempre Minor: non si propone
	// nemmeno l'opzione, invece di farla scegliere e poi rifiutarla con un 400.
	const majorAllowedFor = (setup) => {
		const division = divisionByKey[`${setup.discipline}|${setup.division}`];
		return division ? division.major_allowed !== false : true;
	};

	const startNew = () => setDraft(emptyFirearm(shootingSports[0]?.value || ""));

	const updateSetup = (index, patch) =>
		setDraft((prev) => ({
			...prev,
			setups: prev.setups.map((setup, i) =>
				i === index ? { ...setup, ...patch } : setup,
			),
		}));

	const addSetup = () => {
		const used = new Set(draftDisciplines);
		const next = shootingSports.find((s) => !used.has(s.value));
		if (!next) return;
		setDraft((prev) => ({
			...prev,
			setups: [
				...prev.setups,
				{
					discipline: next.value,
					division: "",
					power_factor: "",
					is_default: false,
				},
			],
		}));
	};

	const removeSetup = (index) =>
		setDraft((prev) => ({
			...prev,
			setups: prev.setups.filter((_, i) => i !== index),
		}));

	const handleSaveFirearm = async () => {
		if (saving || !draft) return;
		if (!draft.nickname.trim()) return toast.error("Dai un nome all'arma");
		if ((draft.setups || []).length === 0) {
			return toast.error("Indica almeno una disciplina in cui usi quest'arma");
		}
		if (draft.setups.some((setup) => !setup.discipline)) {
			return toast.error("Ogni configurazione deve avere una disciplina");
		}

		setSaving(true);
		try {
			const payload = {
				...draft,
				// La division è **opzionale**: non tutte le discipline ne hanno (i
				// Tactical Games classificano l'atleta, non l'arma). Si invia null
				// invece di stringa vuota per non far fallire la validazione.
				setups: draft.setups.map((setup) => ({
					discipline: setup.discipline,
					division: setup.division || null,
					power_factor: setup.power_factor || null,
					is_default: !!setup.is_default,
				})),
			};
			if (draft.firearm_id) {
				await fetchAPI(`${firearmsBase}/${draft.firearm_id}`, {
					method: "PUT",
					body: JSON.stringify(payload),
				});
			} else {
				await fetchAPI(firearmsBase, {
					method: "POST",
					body: JSON.stringify(payload),
				});
			}
			toast.success("Arma salvata");
			setDraft(null);
			await load();
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		} finally {
			setSaving(false);
		}
	};

	// Il default è per (disciplina, tipo): si marca la singola configurazione,
	// non l'arma intera, che può essere predefinita in una disciplina e non in
	// un'altra.
	const handleSetDefault = async (firearm, discipline) => {
		try {
			await fetchAPI(`${firearmsBase}/${firearm.firearm_id}`, {
				method: "PUT",
				body: JSON.stringify({
					setups: setupsOf(firearm).map((setup) => ({
						...setup,
						division: setup.division || null,
						power_factor: setup.power_factor || null,
						is_default: setup.discipline === discipline,
					})),
				}),
			});
			toast.success(`Predefinita per ${sportLabel(discipline)}`);
			await load();
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		}
	};

	const handleDelete = async (firearm) => {
		try {
			const result = await fetchAPI(`${firearmsBase}/${firearm.firearm_id}`, {
				method: "DELETE",
			});
			toast.success(
				result?.deactivated
					? "Arma disattivata: ha punteggi collegati e lo storico li conserva"
					: "Arma eliminata",
			);
			await load();
		} catch (error) {
			toast.error(`Errore: ${error.message}`);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
				<Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
			</div>
		);
	}

	if (shootingSports.length === 0) {
		return (
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-4">
					<p className="text-sm text-muted-foreground">
						Nessuna disciplina di tiro selezionata. Aggiungine una dalla scheda
						Profilo per configurare classe di merito e armi.
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-4">
			{/* ── Classe di merito, una per disciplina ── */}
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-4 space-y-3">
					<p className="label-tactical">Classe di merito federale</p>
					<p className="text-xs text-muted-foreground">
						Quella attualmente assegnata dalla federazione. Cambia nel tempo: i
						punteggi già registrati conservano la classe che avevi al momento.
					</p>
					{shootingSports.map((sport) => (
						<div
							key={`cls_${sport.value}`}
							className="grid grid-cols-[1fr,10rem] items-center gap-2"
						>
							<span className="text-sm">{sport.label}</span>
							<Select
								value={shootingClasses[sport.value] || "__none__"}
								onValueChange={(v) =>
									onClassesChange?.({
										...shootingClasses,
										[sport.value]: v === "__none__" ? "" : v,
									})
								}
								disabled={readOnly}
							>
								<SelectTrigger className="bg-secondary border-transparent rounded-sm">
									<SelectValue placeholder="—" />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="__none__">Non assegnata</SelectItem>
									{classesFor(sport.value).map((c) => (
										<SelectItem key={c.class_id} value={c.value}>
											{c.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					))}
				</CardContent>
			</Card>

			{/* ── Armi ── */}
			<Card className="bg-card border-border rounded-sm">
				<CardContent className="p-4 space-y-3">
					<div className="flex items-center justify-between">
						<div>
							<p className="label-tactical">Le mie armi</p>
							<p className="text-xs text-muted-foreground">
								In inserimento punteggio scegli l'arma e la division si ricava
								da lei.
							</p>
						</div>
						{!readOnly && !draft && (
							<Button
								size="sm"
								className="rounded-sm bg-volt hover:bg-volt/90"
								onClick={startNew}
							>
								<Plus className="w-4 h-4 mr-1.5" />
								Aggiungi
							</Button>
						)}
					</div>

					{firearms.length === 0 && !draft && (
						<p className="text-sm text-muted-foreground">
							Nessuna arma configurata.
						</p>
					)}

					{firearms.map((firearm) => (
						<div
							key={firearm.firearm_id}
							className="p-3 bg-secondary rounded-sm space-y-2"
						>
							<div className="flex items-center justify-between gap-3">
								<div className="flex items-center gap-2 min-w-0">
									{(() => {
										// Pistola o carabina: la silhouette distingue a colpo
										// d'occhio le due armi di un atleta Tactical Games.
										const WeaponIcon = firearmTypeIcon(
											firearm.firearm_type || "handgun",
										);
										return (
											<WeaponIcon size={18} className="text-volt shrink-0" />
										);
									})()}
									<p className="font-medium truncate">{firearm.nickname}</p>
									<Badge className="bg-muted text-muted-foreground border-0 text-[10px]">
										{typeLabel(firearm.firearm_type || "handgun")}
									</Badge>
									{firearm.caliber && (
										<span className="text-xs text-muted-foreground">
											{firearm.caliber}
										</span>
									)}
								</div>
								{!readOnly && (
									<div className="flex items-center gap-1 shrink-0">
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm"
											title="Modifica"
											onClick={() =>
												setDraft({ ...firearm, setups: setupsOf(firearm) })
											}
										>
											<Plus className="w-4 h-4 rotate-45" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="rounded-sm hover:text-red-500"
											onClick={() => handleDelete(firearm)}
										>
											<Trash2 className="w-4 h-4" />
										</Button>
									</div>
								)}
							</div>

							{/* Una riga per disciplina: la stessa arma può essere
							    classificata diversamente in ciascuna. */}
							{setupsOf(firearm).map((setup) => (
								<div
									key={`${firearm.firearm_id}_${setup.discipline}`}
									className="flex items-center justify-between gap-2 pl-6 text-xs"
								>
									<span className="text-muted-foreground truncate">
										{sportLabel(setup.discipline)}
										{setup.division
											? ` · ${divisionLabel(setup.discipline, setup.division)}`
											: " · nessuna division"}
										{setup.power_factor
											? ` · ${setup.power_factor === "major" ? "Major" : "Minor"}`
											: ""}
									</span>
									{setup.is_default ? (
										<Badge className="bg-volt/20 text-volt border-0 text-[10px] shrink-0">
											Predefinita
										</Badge>
									) : (
										!readOnly && (
											<Button
												variant="ghost"
												size="sm"
												className="rounded-sm h-6 text-[10px] shrink-0"
												onClick={() =>
													handleSetDefault(firearm, setup.discipline)
												}
											>
												<Star className="w-3 h-3 mr-1" />
												Predefinita
											</Button>
										)
									)}
								</div>
							))}
						</div>
					))}

					{draft && (
						<div className="p-3 border border-border rounded-sm space-y-3">
							<div className="space-y-2">
								<Label>Nome</Label>
								<Input
									value={draft.nickname}
									onChange={(e) =>
										setDraft({ ...draft, nickname: e.target.value })
									}
									className="bg-secondary border-transparent rounded-sm"
									placeholder="Es: Tanfoglio Stock II"
								/>
							</div>

							<div className="grid grid-cols-2 gap-2">
								<div className="space-y-2">
									<Label>Tipo</Label>
									<Select
										value={draft.firearm_type || "handgun"}
										onValueChange={(v) =>
											setDraft({ ...draft, firearm_type: v })
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											{typesFor(draftDisciplines).map((t) => (
												<SelectItem key={t.value} value={t.value}>
													{t.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="space-y-2">
									<Label>Calibro</Label>
									<Input
										value={draft.caliber || ""}
										onChange={(e) =>
											setDraft({ ...draft, caliber: e.target.value })
										}
										className="bg-secondary border-transparent rounded-sm"
										placeholder="9x21"
									/>
								</div>
							</div>

							{/* Una configurazione per disciplina: la stessa arma può
							    essere Production Minor in IPSC e senza division nei
							    Tactical Games, dove si classifica l'atleta. */}
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label>Dove la uso</Label>
									{draft.setups.length < shootingSports.length && (
										<Button
											type="button"
											size="sm"
											variant="outline"
											className="rounded-sm"
											onClick={addSetup}
										>
											<Plus className="w-3.5 h-3.5 mr-1" /> Disciplina
										</Button>
									)}
								</div>

								{draft.setups.length === 0 && (
									<p className="text-xs text-amber-400">
										Aggiungi almeno una disciplina.
									</p>
								)}

								{draft.setups.map((setup, index) => {
									const divisions = divisionsFor(setup.discipline);
									const majorAllowed = majorAllowedFor(setup);
									return (
										<div
											key={`setup_${index}`}
											className="p-2 border border-border rounded-sm space-y-2"
										>
											<div className="flex items-center gap-2">
												<Select
													value={setup.discipline || "__none__"}
													onValueChange={(v) =>
														updateSetup(index, {
															discipline: v === "__none__" ? "" : v,
															// Division e power factor appartengono alla
															// disciplina: cambiandola non valgono più.
															division: "",
															power_factor: "",
														})
													}
												>
													<SelectTrigger className="bg-secondary border-transparent rounded-sm">
														<SelectValue placeholder="Disciplina" />
													</SelectTrigger>
													<SelectContent className="bg-card border-border">
														{shootingSports.map((sport) => (
															<SelectItem key={sport.value} value={sport.value}>
																{sport.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="rounded-sm hover:text-red-500 h-8 w-8 shrink-0"
													onClick={() => removeSetup(index)}
												>
													<Trash2 className="w-3.5 h-3.5" />
												</Button>
											</div>

											{divisions.length === 0 ? (
												<p className="text-xs text-muted-foreground">
													Questa disciplina non ha division configurate: non
													serve indicarla.
												</p>
											) : (
												<div className="grid grid-cols-2 gap-2">
													<Select
														value={setup.division || "__none__"}
														onValueChange={(v) =>
															updateSetup(index, {
																division: v === "__none__" ? "" : v,
																power_factor: "",
															})
														}
													>
														<SelectTrigger className="bg-secondary border-transparent rounded-sm">
															<SelectValue placeholder="Division" />
														</SelectTrigger>
														<SelectContent className="bg-card border-border">
															{/* Opzionale: si può non indicarla. */}
															<SelectItem value="__none__">
																Nessuna division
															</SelectItem>
															{divisions.map((d) => (
																<SelectItem key={d.division_id} value={d.value}>
																	{d.label}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
													<Select
														value={setup.power_factor || "__auto__"}
														onValueChange={(v) =>
															updateSetup(index, {
																power_factor: v === "__auto__" ? "" : v,
															})
														}
													>
														<SelectTrigger className="bg-secondary border-transparent rounded-sm">
															<SelectValue />
														</SelectTrigger>
														<SelectContent className="bg-card border-border">
															<SelectItem value="__auto__">
																{setup.division
																	? "Dalla division"
																	: "Non indicato"}
															</SelectItem>
															{majorAllowed && (
																<SelectItem value="major">Major</SelectItem>
															)}
															<SelectItem value="minor">Minor</SelectItem>
														</SelectContent>
													</Select>
												</div>
											)}

											{setup.division && !majorAllowed && (
												<p className="text-xs text-muted-foreground">
													Questa division si scora sempre Minor.
												</p>
											)}

											<Button
												type="button"
												size="sm"
												variant={setup.is_default ? "default" : "secondary"}
												className={`rounded-sm w-full ${setup.is_default ? "bg-volt hover:bg-volt/90" : ""}`}
												onClick={() =>
													updateSetup(index, { is_default: !setup.is_default })
												}
											>
												<Star className="w-3.5 h-3.5 mr-1.5" />
												Predefinita per questa disciplina
											</Button>
										</div>
									);
								})}
							</div>

							<div className="grid grid-cols-2 gap-2">
								<Input
									value={draft.make || ""}
									onChange={(e) => setDraft({ ...draft, make: e.target.value })}
									className="bg-secondary border-transparent rounded-sm"
									placeholder="Marca"
								/>
								<Input
									value={draft.optic || ""}
									onChange={(e) =>
										setDraft({ ...draft, optic: e.target.value })
									}
									className="bg-secondary border-transparent rounded-sm"
									placeholder="Ottica (se presente)"
								/>
							</div>

							<div className="flex items-center gap-2">
								<div className="ml-auto flex gap-2">
									<Button
										variant="outline"
										size="sm"
										className="rounded-sm"
										onClick={() => setDraft(null)}
										disabled={saving}
									>
										Annulla
									</Button>
									<Button
										size="sm"
										className="rounded-sm bg-volt hover:bg-volt/90"
										onClick={handleSaveFirearm}
										disabled={saving}
									>
										{saving ? (
											<Loader2 className="w-4 h-4 animate-spin" />
										) : (
											"Salva"
										)}
									</Button>
								</div>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
